import {FilterIcon, SearchIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, CounterLabel, IconButton, Spinner, TextInput} from '@primer/react'
import {useState, type ReactNode} from 'react'
import {clsx} from 'clsx'
import styles from './FileFilter.module.css'

export const NO_FILE_EXTENSION = 'No extension'

type FileFilterInputProps = {
  filterSize?: 'small' | 'medium'
  filterText?: string
  onFilterTextChange?: (filterText: string) => void
}

export function FileFilterLoading({filterText, onFilterTextChange}: FileFilterInputProps) {
  return (
    <div className="d-flex flex-nowrap gap-2">
      <TextInput
        block
        aria-label="Filter files…"
        leadingVisual={SearchIcon}
        placeholder="Filter files…"
        value={filterText}
        onChange={event => onFilterTextChange?.(event.target.value)}
      />
      <ActionMenu>
        <ActionMenu.Anchor>
          <IconButton aria-label="Filter options" icon={FilterIcon} className="flex-shrink-0" />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay className={clsx(styles.filterLoadingHeight)}>
          <div
            className={clsx(
              'd-flex',
              'flex-column',
              'flex-justify-center',
              'flex-items-center',
              styles.filterLoadingHeight,
            )}
          >
            <Spinner size="medium" />
          </div>
        </ActionMenu.Overlay>
      </ActionMenu>
    </div>
  )
}

export type FileFilterBaseProps = {
  fileExtensions?: Record<string, number>
  unselectedFileExtensions?: Set<string>
  onFilterChange?: (type: 'selectFileExtension' | 'unselectFileExtension', payload: {extension: string}) => void
  onClearAllFilters?: () => void
  additionalFilterGroups?: ReactNode
  isFilteringActive?: boolean
  ref?: React.Ref<HTMLInputElement>
} & FileFilterInputProps

export function FileFilterShared({
  ref,
  filterSize = 'medium',
  filterText: initialFilterText,
  onFilterTextChange,
  fileExtensions,
  unselectedFileExtensions,
  onFilterChange,
  onClearAllFilters,
  additionalFilterGroups,
  isFilteringActive,
}: FileFilterBaseProps) {
  fileExtensions = fileExtensions ?? {}
  unselectedFileExtensions = unselectedFileExtensions ?? new Set<string>()
  const [filterText, setFilterText] = useState(initialFilterText ?? '')

  const setSelectedFileFilters = (select: boolean, extension: string) => {
    const action = select ? 'selectFileExtension' : 'unselectFileExtension'
    onFilterChange?.(action, {extension})
  }

  const clearFilters = () => {
    setFilterText('')
    onClearAllFilters?.()
  }

  const extensions = Object.keys(fileExtensions)
    .filter(ext => ext !== NO_FILE_EXTENSION)
    .sort()
  const fileExtensionIsSelected = (ext: string) => !unselectedFileExtensions.has(ext)
  const noExtensionPresent = !!fileExtensions[NO_FILE_EXTENSION]

  return (
    <div id="diff-file-tree-filter" className="d-flex flex-nowrap gap-2">
      <TextInput
        ref={ref}
        block
        aria-label="Filter files…"
        leadingVisual={SearchIcon}
        placeholder="Filter files…"
        size={filterSize}
        className={clsx(filterSize === 'small' && styles.filterMaxHeight)}
        value={filterText}
        onChange={event => {
          setFilterText(event.target.value)
          onFilterTextChange?.(event.target.value)
        }}
      />
      <div className="position-relative">
        <ActionMenu>
          <ActionMenu.Anchor>
            <IconButton aria-label="Filter options" icon={FilterIcon} className="flex-shrink-0" size={filterSize} />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>
              <ActionList.Group selectionVariant="multiple">
                <ActionList.GroupHeading>File extensions</ActionList.GroupHeading>
                {extensions.map((ext: string) => {
                  const selected = fileExtensionIsSelected(ext)
                  return (
                    <ActionList.Item
                      key={ext}
                      selected={selected}
                      onSelect={() => setSelectedFileFilters(!selected, ext)}
                    >
                      {ext}
                      <ActionList.TrailingVisual>
                        <CounterLabel>{fileExtensions[ext]}</CounterLabel>
                      </ActionList.TrailingVisual>
                    </ActionList.Item>
                  )
                })}
                {noExtensionPresent && (
                  <ActionList.Item
                    key={NO_FILE_EXTENSION}
                    selected={fileExtensionIsSelected(NO_FILE_EXTENSION)}
                    onSelect={() =>
                      setSelectedFileFilters(!fileExtensionIsSelected(NO_FILE_EXTENSION), NO_FILE_EXTENSION)
                    }
                  >
                    {NO_FILE_EXTENSION}
                    <ActionList.TrailingVisual>
                      <CounterLabel>{fileExtensions[NO_FILE_EXTENSION]}</CounterLabel>
                    </ActionList.TrailingVisual>
                  </ActionList.Item>
                )}
              </ActionList.Group>
              {additionalFilterGroups}

              {isFilteringActive && onClearAllFilters && (
                <>
                  <ActionList.Divider />
                  <ActionList.Item onSelect={clearFilters}>
                    <span className={styles.clearFiltersText}>Clear filters</span>
                  </ActionList.Item>
                </>
              )}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
        {isFilteringActive && <span className={styles.filterIndicator} />}
      </div>
    </div>
  )
}
